import {
  Permissions,
  PracticeQueryParams,
} from 'constants/application-constants'

import { useMemo } from 'react'

import EditIcon from 'assets/Icons/EditIcon'
import {
  Button,
  Card,
  LoadingIndicator,
  Modal,
  Typography,
} from 'components/common'
import { createReadableMetadata, practiceModalCloseLocation } from 'helpers'
import { useAppSelector, usePermissions, useFrontOfficeCheck } from 'hooks'
import { t } from 'i18next'
import { X } from 'react-feather'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'

import styles from './MetadataDetailsModal.module.scss'
import MetadataListProperty from './MetadataListProperty'
import MetadataProperty from './MetadataProperty'
import {
  practicesApi,
  selectCurrentRevision,
  selectIsCurrentRevisionLoading,
} from '../../api'

const MetadataDetailsModal = () => {
  const currentRevision = useAppSelector(selectCurrentRevision)
  const currentRevisionLoading = useAppSelector(selectIsCurrentRevisionLoading)
  const { id: practiceId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const isFrontOffice = useFrontOfficeCheck()
  const [searchParams] = useSearchParams()
  const revisionStatus = searchParams.get(PracticeQueryParams.REVISION_STATUS)

  const backgroundLink = practiceModalCloseLocation(
    isFrontOffice,
    location,
    practiceId ?? ''
  )

  const { mixed: allPermissions } = usePermissions(practiceId)

  const httpGetExistingPractices =
    practicesApi.useGetExistingPracticesQuery(null)

  const canEditDetails =
    !isFrontOffice &&
    !!revisionStatus &&
    revisionStatus !== 'published' &&
    allPermissions !== null &&
    allPermissions[Permissions.UPDATE_PRACTICE]

  const propertyData = useMemo(() => {
    if (!httpGetExistingPractices.isSuccess) {
      return null
    }

    const metadata = currentRevision?.metadata.find(
      ({ language }) =>
        searchParams.get(PracticeQueryParams.METADATA_LANGUAGE) ===
        language.code
    )
    if (metadata) {
      return createReadableMetadata(
        metadata,
        currentRevision,
        httpGetExistingPractices.data.data
      )
    }

    return null
  }, [
    currentRevision,
    searchParams,
    httpGetExistingPractices.data,
    httpGetExistingPractices.isSuccess,
  ])

  // const author = (() => {
  //   if (currentRevision && currentRevision?.contact_users?.length > 0)
  //     return currentRevision.contact_users[0]
  // })()

  const handleCloseModal = () => {
    if (practiceId) {
      navigate(backgroundLink, { replace: true })
    } else {
      navigate(-1)
    }
  }

  const handleEditClick = () => {
    const backgroundLink = location.pathname.split('/details')[0]
    navigate({
      pathname: `${backgroundLink}/edit`,
      hash: location.hash,
      search: location.search,
    })
  }

  return (
    <Modal open handleClose={handleCloseModal} className={styles.modal}>
      <Card className={styles.card}>
        <Card.Header className={styles.cardHeader}>
          <div className={styles.cardTitle}>
            <Typography
              type="display"
              size="x-small"
              fontWeight="bold"
              element="h1"
            >
              {t('metadata.details')}
            </Typography>
            {canEditDetails && (
              <Button
                variant="text"
                onClick={handleEditClick}
                size="x-small"
                icon={<EditIcon />}
                iconLabel={t('metadata.editAria') ?? ''}
              />
            )}
          </div>

          <Button
            size="x-small"
            icon={<X />}
            variant="text"
            iconLabel={t('metadata.closeAria') ?? ''}
            onClick={handleCloseModal}
          />
        </Card.Header>
        <Card.Header className={styles.cardHeaderMobile}>
          <Typography
            type="text"
            fontWeight="bold"
            className={styles.title}
            element="h1"
          >
            {t('metadata.details')}
          </Typography>
          <Button
            className={styles.btnClose}
            size="x-small"
            variant="text"
            icon={<X />}
            iconLabel={t('metadata.closeAria') ?? ''}
            onClick={handleCloseModal}
          />
        </Card.Header>
        <Card.Body className={styles.cardBody}>
          {(currentRevisionLoading || !currentRevision) && (
            <div>
              <LoadingIndicator />
            </div>
          )}
          {!currentRevisionLoading && !!propertyData && (
            <>
              <div className={styles.section}>
                <Typography fontWeight="medium" element="h2" size="large">
                  {t('metadata.general')}
                </Typography>
                <div>
                  {propertyData.general?.map(({ title, value }) => (
                    <MetadataProperty
                      key={title}
                      title={t(title)}
                      value={value}
                    />
                  ))}
                </div>
              </div>

              <div className={styles.section}>
                <Typography fontWeight="medium" element="h2" size="large">
                  {t('metadata.gentopic')}
                </Typography>
                <div className={styles.twoColumns}>
                  {propertyData.topic.map(({ title, value }) => (
                    <MetadataProperty
                      key={title}
                      title={t(title)}
                      value={value}
                    />
                  ))}
                </div>
              </div>

              <div className={styles.section}>
                <Typography fontWeight="medium" element="h2" size="large">
                  {t('metadata.scope')}
                </Typography>

                <div className={styles.twoColumns}>
                  {propertyData.scope?.map(({ title, value }) => (
                    <MetadataProperty
                      key={title}
                      title={t(title)}
                      value={value}
                    />
                  ))}
                </div>
              </div>

              <div className={styles.section}>
                <Typography fontWeight="medium" element="h2" size="large">
                  {t('metadata.references')}
                </Typography>

                <div className={styles.twoColumns}>
                  {propertyData.references?.map(({ title, value }) => (
                    <>
                      {title !== 'metadata.externalReferences' && (
                        <MetadataProperty
                          key={title}
                          title={t(title)}
                          value={value}
                        />
                      )}

                      {title === 'metadata.externalReferences' && (
                        <MetadataListProperty
                          key={title}
                          title={t(title)}
                          values={value}
                        />
                      )}
                    </>
                  ))}
                </div>
              </div>
            </>
          )}

          {/*{!!author && (*/}
          {/*  <div className={styles.contact}>*/}
          {/*    <div className={styles.contactInfo}>*/}
          {/*      <Avatar*/}
          {/*        src={author?.avatar_public_path}*/}
          {/*        content={author?.initials ?? ''}*/}
          {/*      />*/}

          {/*      <div className={styles.authorDescription}>*/}
          {/*        <Typography fontWeight="medium">{author?.name}</Typography>*/}
          {/*        <Typography color="label">{t('practice.author')}</Typography>*/}
          {/*      </div>*/}
          {/*    </div>*/}

          {/*    {!!author?.email && (*/}
          {/*      <Button*/}
          {/*        to={`mailto:${author?.email}`}*/}
          {/*        as="link"*/}
          {/*        size="small"*/}
          {/*        variant="subtle"*/}
          {/*        icon={<Mail />}*/}
          {/*      >*/}
          {/*        {t('practice.toContact')}*/}
          {/*      </Button>*/}
          {/*    )}*/}
          {/*  </div>*/}
          {/*)}*/}
        </Card.Body>
      </Card>
    </Modal>
  )
}

export default MetadataDetailsModal
